/**
 * File: client/src/components/HomeRunTab.js
 * ==========================================
 * HomeRun - Home Maintenance Module
 *
 * Clean, scannable maintenance dashboard for homeowners.
 *
 * Sections:
 * 1. Home Health Score (overall maintenance status)
 * 2. Upcoming Tasks (next 30 days)
 * 3. Task Categories (organized by type)
 * 4. Quick Add Task
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  CheckCircle2,
  Circle,
  AlertTriangle,
  Wrench,
  Flame,
  Droplets,
  Zap,
  Shield,
  Leaf,
  FileText,
  Plus,
  ChevronRight,
  Calendar,
  X,
  ShieldCheck,
  Building2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext.jsx";

/**
 * Category definitions with icons and colors
 */
const CATEGORIES = {
  maintenance: { label: "Maintenance", icon: Wrench, color: "text-orange-600", bg: "bg-orange-50" },
  hvac: { label: "HVAC", icon: Flame, color: "text-red-600", bg: "bg-red-50" },
  plumbing: { label: "Plumbing", icon: Droplets, color: "text-blue-600", bg: "bg-blue-50" },
  electrical: { label: "Electrical", icon: Zap, color: "text-yellow-600", bg: "bg-yellow-50" },
  safety: { label: "Safety", icon: Shield, color: "text-rose-600", bg: "bg-rose-50" },
  exterior: { label: "Exterior", icon: Leaf, color: "text-green-600", bg: "bg-green-50" },
  insurance: { label: "Insurance", icon: ShieldCheck, color: "text-purple-600", bg: "bg-purple-50" },
  mortgage: { label: "Mortgage", icon: Building2, color: "text-indigo-600", bg: "bg-indigo-50" },
  documents: { label: "Documents", icon: FileText, color: "text-slate-600", bg: "bg-slate-50" },
  general: { label: "General", icon: Wrench, color: "text-gray-600", bg: "bg-gray-50" },
};

const PRIORITIES = {
  high: { label: "High", color: "bg-red-500" },
  medium: { label: "Medium", color: "bg-amber-500" },
  low: { label: "Low", color: "bg-green-500" },
};

/**
 * Calculate days until a date
 */
const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
};

/**
 * Home Health Score Card
 */
function HealthScore({ tasks }) {
  const total = tasks.length;
  const completed = tasks.filter(t => t.status === "Completed").length;
  const overdue = tasks.filter(t => {
    if (t.status === "Completed") return false;
    const days = daysUntil(t.dueDate);
    return days !== null && days < 0;
  }).length;
  const highPriority = tasks.filter(t => t.priority === "High" && t.status !== "Completed").length;

  // Calculate score (100 = perfect, deduct for issues)
  let score = 100;
  score -= overdue * 10; // -10 per overdue task
  score -= highPriority * 5; // -5 per high priority open task
  score = Math.max(0, Math.min(100, score));

  const getScoreColor = () => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreLabel = () => {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    if (score >= 40) return "Needs Attention";
    return "Action Required";
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Home Health</h3>
        <span className={`text-sm font-medium ${getScoreColor()}`}>{getScoreLabel()}</span>
      </div>

      {/* Score Circle */}
      <div className="flex items-center gap-6">
        <div className="relative w-24 h-24">
          <svg className="w-24 h-24 transform -rotate-90">
            <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="8" fill="none" />
            <circle
              cx="48"
              cy="48"
              r="40"
              stroke={score >= 80 ? "#22c55e" : score >= 60 ? "#f59e0b" : "#ef4444"}
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${(score / 100) * 251.2} 251.2`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-2xl font-bold ${getScoreColor()}`}>{score}</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex-1 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Tasks Completed</span>
            <span className="font-medium text-gray-900">{completed}/{total}</span>
          </div>
          {overdue > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-red-600">Overdue</span>
              <span className="font-medium text-red-600">{overdue}</span>
            </div>
          )}
          {highPriority > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-amber-600">High Priority</span>
              <span className="font-medium text-amber-600">{highPriority}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Upcoming Tasks - Next 30 days
 */
function UpcomingTasks({ tasks, onToggle }) {
  const upcoming = tasks
    .filter(t => {
      if (t.status === "Completed") return false;
      const days = daysUntil(t.dueDate);
      return days !== null && days >= 0 && days <= 30;
    })
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    .slice(0, 5);

  if (upcoming.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-[#CC0202]" />
          <h3 className="text-lg font-semibold text-gray-900">Upcoming (30 days)</h3>
        </div>
        <p className="text-gray-500 text-sm">No upcoming tasks scheduled.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-[#CC0202]" />
        <h3 className="text-lg font-semibold text-gray-900">Upcoming (30 days)</h3>
      </div>

      <div className="space-y-3">
        {upcoming.map((task) => {
          const days = daysUntil(task.dueDate);
          const category = CATEGORIES[task.category?.toLowerCase()] || CATEGORIES.general;
          const Icon = category.icon;

          return (
            <div
              key={task.id}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => onToggle(task.id)}
            >
              <button className="flex-shrink-0">
                <Circle className="w-5 h-5 text-gray-300 hover:text-gray-400" />
              </button>
              <Icon className={`w-4 h-4 ${category.color} flex-shrink-0`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                <p className="text-xs text-gray-500">{category.label}</p>
              </div>
              <div className={`text-xs font-medium ${days <= 7 ? "text-amber-600" : "text-gray-500"}`}>
                {days === 0 ? "Today" : days === 1 ? "Tomorrow" : `${days} days`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Task List by Category
 */
function TaskList({ tasks, onToggle, onAddTask }) {
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Group tasks by category
  const grouped = tasks.reduce((acc, task) => {
    const cat = task.category?.toLowerCase() || "general";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(task);
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-[#CC0202]" />
          <h3 className="text-lg font-semibold text-gray-900">All Tasks</h3>
        </div>
        <button
          onClick={onAddTask}
          className="flex items-center gap-1 text-sm text-[#CC0202] font-medium hover:underline"
        >
          <Plus className="w-4 h-4" />
          Add Task
        </button>
      </div>

      <div className="space-y-2">
        {Object.entries(CATEGORIES).map(([key, category]) => {
          const catTasks = grouped[key] || [];
          const completedCount = catTasks.filter(t => t.status === "Completed").length;
          const isExpanded = expandedCategory === key;
          const Icon = category.icon;

          if (catTasks.length === 0) return null;

          return (
            <div key={key} className="border border-gray-100 rounded-xl overflow-hidden">
              {/* Category Header */}
              <button
                className={`w-full flex items-center justify-between p-4 ${category.bg} hover:opacity-90 transition-opacity`}
                onClick={() => setExpandedCategory(isExpanded ? null : key)}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${category.color}`} />
                  <span className="font-medium text-gray-900">{category.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">
                    {completedCount}/{catTasks.length}
                  </span>
                  <ChevronRight
                    className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                  />
                </div>
              </button>

              {/* Task Items */}
              {isExpanded && (
                <div className="p-2 space-y-1 bg-white">
                  {catTasks.map((task) => {
                    const isCompleted = task.status === "Completed";
                    const days = daysUntil(task.dueDate);
                    const isOverdue = days !== null && days < 0 && !isCompleted;

                    return (
                      <div
                        key={task.id}
                        className={`flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ${
                          isCompleted ? "opacity-60" : ""
                        }`}
                        onClick={() => onToggle(task.id)}
                      >
                        <button className="flex-shrink-0">
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${isCompleted ? "line-through text-gray-400" : "text-gray-900"}`}>
                            {task.title}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {task.priority && !isCompleted && (
                            <div className={`w-2 h-2 rounded-full ${PRIORITIES[task.priority.toLowerCase()]?.color || "bg-gray-300"}`} />
                          )}
                          {isOverdue && (
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                          )}
                          {days !== null && !isCompleted && !isOverdue && (
                            <span className="text-xs text-gray-400">
                              {days === 0 ? "Today" : `${days}d`}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Quick Add Task Modal
 */
function AddTaskModal({ isOpen, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("general");
  const [priority, setPriority] = useState("Medium");
  const [dueDate, setDueDate] = useState("");

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      category: category.charAt(0).toUpperCase() + category.slice(1),
      priority,
      dueDate: dueDate || null,
    });
    setTitle("");
    setCategory("general");
    setPriority("Medium");
    setDueDate("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Add Task</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <input
          type="text"
          placeholder="Task title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#CC0202]/20 focus:border-[#CC0202]"
          autoFocus
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-500 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#CC0202]/20"
            >
              {Object.entries(CATEGORIES).map(([key, cat]) => (
                <option key={key} value={key}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#CC0202]/20"
            >
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-500 mb-1">Due Date (optional)</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#CC0202]/20"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!title.trim()}
            className="flex-1 px-4 py-3 bg-[#CC0202] text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
          >
            Add Task
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * HomeRunTab - Main Component
 */
export default function HomeRunTab({ propertyId }) {
  const { getToken } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      const res = await fetch(`/api/tasks/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data);
      }
    } catch (err) {
      console.error("Failed to load tasks:", err);
    } finally {
      setLoading(false);
    }
  }, [propertyId, getToken]);

  useEffect(() => {
    if (propertyId) fetchTasks();
  }, [propertyId, fetchTasks]);

  // Toggle task completion
  const handleToggle = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const newStatus = task.status === "Completed" ? "Open" : "Completed";

    // Optimistic update
    setTasks(prev =>
      prev.map(t => (t.id === taskId ? { ...t, status: newStatus } : t))
    );

    try {
      const token = getToken();
      await fetch(`/api/tasks/${propertyId}/${taskId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...task, status: newStatus }),
      });
    } catch (err) {
      console.error("Failed to update task:", err);
      fetchTasks(); // Revert on error
    }
  };

  // Add new task
  const handleAddTask = async (taskData) => {
    try {
      const token = getToken();
      const res = await fetch(`/api/tasks/${propertyId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...taskData,
          status: "Open",
        }),
      });

      if (res.ok) {
        fetchTasks();
      }
    } catch (err) {
      console.error("Failed to add task:", err);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 py-6 animate-pulse">
        <div className="h-32 bg-gray-200 rounded-2xl" />
        <div className="h-48 bg-gray-200 rounded-2xl" />
        <div className="h-64 bg-gray-200 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 py-6">
      {/* Row 1: Health Score + Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <HealthScore tasks={tasks} />
        <UpcomingTasks tasks={tasks} onToggle={handleToggle} />
      </div>

      {/* Row 2: All Tasks */}
      <TaskList
        tasks={tasks}
        onToggle={handleToggle}
        onAddTask={() => setShowAddModal(true)}
      />

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddTask}
      />
    </div>
  );
}
